import React, { useState } from "react";
import {
  Button,
  Container,
  Form,
  Modal
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import { updateSpot, getSpotStaff } from "src/Services/api";
import useMutation from 'src/Hooks/useMutation';
import useQuery from 'src/Hooks/useQuery';
import confirm from "src/Components/ConfirmDialog";
import JamlyfTable from "src/Components/Table";
import LoadingOverlay from "src/Components/LoadingOverlay";
import { isSearchableString } from 'src/Utils/queryHelpers';
import SearchBox from 'src/Components/SearchBox';
import Notification, { useNotification } from 'src/Components/Notification';
import "./style.css";

const STAFF_HEADER_TABLE_MAPPER = {
  first_name: "first name",
  last_name: "last name",
  email: "email",
  phone_no: "phone number",
  role: "role"
};

const getStaffQueryParams = (id, searchTerm, pageNumber) => {
  const queryParams = { id }
  if (!!searchTerm && isSearchableString(searchTerm)) {
    queryParams["search"] = searchTerm;
  }
  if (!!pageNumber) {
    queryParams["page"] = pageNumber;
  }
  return queryParams;
}

const StaffListPage = () => {
  const [staffToUpdate, setStaffToUpdate] = useState({});
  const [searchStr, setSearchStr] = useState("");
  const [pageNum, setPageNum] = useState(1);
  const [show, setShow] = useState(false);
  let { id } = useParams();
  const navigate = useNavigate();
  const { notifyError, notifySuccess, onClose, notification } = useNotification();

  const queryParams = getStaffQueryParams(id, searchStr, pageNum);
  const { loading, response = {}, refetch: refetchStaff} = useQuery(getSpotStaff, queryParams)
  const { data = {} } = response;
  const { objects: paginatedData = [], total_objects, total_pages } = data;

  const {
    setter: updateStaffAction,
    response: staffUpadateResponse,
    loading: updateStaffLoading
  } = useMutation(updateSpot);

  const {
    setter: deleteStaffAction,
    loading: deleteStaffLoading
  } = useMutation(updateSpot);

  // Change keys with spaces to underscore
  const cleanData = staff =>
    Object.fromEntries(Object.entries(staff).map(([key, value]) => [(key).replace(/ /g, '_'), value]));

  const handleDesignationSelect = val => {
    setStaffToUpdate(prevState => ({
      ...prevState, role: val
    }));
  };

  const handleStaffEdit = (staff) => {
    setShow(true);
    setStaffToUpdate(cleanData(staff));
  };

  const handleStaffUpdate = async () => {
    const data = { mode: "change", staff: staffToUpdate };
    updateStaffAction(
      { id, data },
      () => {
        notifySuccess({ message: staffUpadateResponse?.data || "Staff updated successfully" });
        refetchStaff();
      },
      () => {
        notifyError({ message: staffUpadateResponse?.response.data.detail || "Error updating staff" });
      }, 
    ) 
    setShow(false);
  };

  const handleStaffDelete = async (staff) => {
    const data = { mode: "delete", staff: cleanData(staff) };
    if (await confirm(`Are you sure you want to remove ${staff.email} from your spot?`)) {
      deleteStaffAction(
        { id, data },
        () => {
          notifySuccess({ message: "Staff removed successfully" });
          refetchStaff();
        },
        () => notifyError({ message: "Staff could not be removed" }),
      );
    }
  };

  return (
    <Container className="mt-4">
      <Notification id="menu-edit" onClose={onClose} {...notification} />
      <div style={{ paddingBottom: 20, paddingTop: 20 }}>
        <div className="d-inline-flex align-items-center">
          <h5 className="mb-0">Staffs</h5>
          <span className="ps-2 fs-6 fw-lighter">
            ({total_objects} {total_objects > 1 ? `staffs` : `staff`})
          </span>
        </div>
        <Button
          className="jamlyfSolidBtn float-end"
          onClick={() => navigate(`new` )}
        >
          Add staff
        </Button>
      </div>
      <div className="d-inline-block col-12 col-lg-8 mb-4">
        <SearchBox
          id="formStaffSearch"
          placeholder="Search staff"
          defaultValue={searchStr}
          onChange={setSearchStr}
          isLoading={loading && isSearchableString(searchStr)}
          onReset={() => setSearchStr("")}
          disabled={!searchStr && !paginatedData?.length}
        />
      </div>
      <LoadingOverlay show={loading || deleteStaffLoading}/>
      <JamlyfTable
        data={paginatedData}
        headers={Object.values(STAFF_HEADER_TABLE_MAPPER)}
        onEditActionClick={handleStaffEdit}
        onDeleteActionClick={handleStaffDelete}
        headerMapper={STAFF_HEADER_TABLE_MAPPER}
        onPageChange={setPageNum}
        totalPages={total_pages}
        showActions
      />
      <Modal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header className="modalHeader" closeButton>
          <Modal.Title>
            {`${staffToUpdate.first_name} ${staffToUpdate.last_name}`.match(/\b(\w)/g).join("")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <Form>
              <Form.Control
                autoFocus
                className="formInput text-dark"
                type="text"
                placeholder="Full name"
                value={`${staffToUpdate.first_name} ${staffToUpdate.last_name}`}
                disabled
              />
              <Form.Control
                className="formInput text-dark"
                type="email"
                placeholder="Email"
                value={staffToUpdate.email}
                disabled
              />
              <Form.Select
                className="formInput text-dark"
                onChange={(e) => handleDesignationSelect(e.target.value)}
                defaultValue={staffToUpdate.role}
              >
                <option value="steward">Steward</option>
                <option value="manager">Manager</option>
              </Form.Select>
            </Form>
          </Container>
        </Modal.Body>
        <Modal.Footer className="modalFooter">
          <Button
            className="jamlyfSolidBtn"
            onClick={() => handleStaffUpdate()}
            disabled={!!updateStaffLoading}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};


export default StaffListPage;
